import {
  Box,
  Button,
  Columns,
  Container,
  Heading,
  Stack,
  Text,
  UnstyledLink,
  Visible,
} from '@spaceship-fspl/components';
import { formatDate } from '@spaceship-fspl/helpers';
import { FeatherArrowRightIcon } from '@spaceship-fspl/icons-web';
import {
  borderColor,
  borderWidthBottom,
  borderWidthTop,
  color,
  marginRight,
  paddingY,
} from '@spaceship-fspl/styles';
import {
  getContentPageLayoutStaticProps,
  getMetadataPropsFromPrismicSlice,
  Metadata,
  MetadataProps,
  withContentPageLayout,
} from 'components';
import {
  getNewsArticles,
  GetNewsArticlesResponse,
  getNewsHomepage,
  mapSlicesToProps,
  RichText,
} from 'data/prismic';
import { useNews } from 'data/prismic/hooks';
import {
  News_HomepageBodyHero,
  News_HomepageBodyHeroPrimary,
} from 'data/prismic/types';
import Link from 'next/link';
import * as React from 'react';
import styled from 'styled-components';

const INITIAL_NEWS_COUNT = 11;

const ArticlesList = styled.ul`
  ${borderColor('neutral.050')}
  ${borderWidthTop('sm')}
  border-top-style: solid;
  list-style: none;
  margin: 0;
  padding: 0;
`;

const ArticlePublishedDate = styled(Text).attrs({
  variant: 1,
  color: 'neutral.080',
  align: { xs: 'left', md: 'right' },
})`
  transition: 0.15s;
`;

const ArticleTitle = styled(Text).attrs({
  variant: 1,
})`
  ${marginRight({ xs: 'none', md: 'sm', lg: 'lg' })}
  flex: 1;
  transition: 0.15s;
`;

const ArticleView = styled(Text).attrs({
  variant: 1,
  color: 'indigo.070',
  whiteSpace: 'nowrap',
})`
  opacity: 0;
`;

const ArticlesLink = styled(UnstyledLink)`
  ${borderColor('neutral.050')}
  ${borderWidthBottom('sm')}
  ${paddingY('md')}
  border-bottom-style: solid;
  display: inline-block;
  width: 100%;

  :hover {
    ${ArticlePublishedDate}, ${ArticleTitle} {
      ${color('indigo.070')}
    }

    ${ArticleView} {
      opacity: 1;
    }
  }
`;

interface NewsHomepageProps {
  data: {
    News_homepageBodyMetadata?: MetadataProps | null;
    News_homepageBodyHero?: News_HomepageBodyHeroPrimary | null;
  };
}

export const getStaticProps =
  getContentPageLayoutStaticProps<NewsHomepageProps>(async () => {
    const document = await getNewsHomepage();
    const data = mapSlicesToProps(document, {
      News_homepageBodyMetadata: (slice) =>
        getMetadataPropsFromPrismicSlice(slice),
      News_homepageBodyHero: (slice: News_HomepageBodyHero) => ({
        ...slice.primary,
      }),
    });

    const news = await getNewsArticles({ limit: INITIAL_NEWS_COUNT });

    return {
      props: {
        data,
        news,
      },
    };
  });

const NewsPage: React.FC<
  React.PropsWithChildren<NewsHomepageProps & { news: GetNewsArticlesResponse }>
> = ({
  data: { News_homepageBodyMetadata: metadata, News_homepageBodyHero: hero },
  news: initialNews,
}) => {
  const {
    data: allNews,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
  } = useNews({
    initialData: initialNews,
    params: { limit: INITIAL_NEWS_COUNT },
  });
  const news: { slug: string; title: string; publishedDate: string }[] = [];

  allNews?.pages.flatMap(({ nodes }) =>
    nodes.forEach(({ _meta, title, published_date }) => {
      if (!_meta.uid || !title || !published_date) {
        return;
      }

      news.push({
        slug: _meta.uid,
        title,
        publishedDate: published_date,
      });
    }),
  );

  return (
    <>
      {metadata && <Metadata {...metadata} />}

      <Box
        marginTop={{ xs: 'lg', lg: 'xl' }}
        marginBottom={{ xs: 'xl', md: 'xxl', lg: 'xxxl' }}
      >
        <Container>
          <Columns alignX="center" spaceY={{ xs: 'lg', lg: 'xl' }}>
            <Columns.Column width={{ xs: 1, lg: 10 / 12 }}>
              <Stack spaceY={{ xs: 'lg', md: 'xl', lg: 'xxl' }}>
                {hero && (
                  <Stack spaceY={{ xs: 'sm', md: 'md', lg: 'sm' }}>
                    <Heading variant={1} align="center" component="h1">
                      {hero.title}
                    </Heading>

                    <Text variant={1} align="center" component="div">
                      <RichText render={hero.richtagline} />
                    </Text>
                  </Stack>
                )}

                <Stack spaceY="xl">
                  <ArticlesList>
                    {news.map(({ slug, title, publishedDate }) => (
                      <li key={slug}>
                        <Link
                          href="/news/[slug]/"
                          as={`/news/${slug}/`}
                          passHref={true}
                          legacyBehavior
                        >
                          <ArticlesLink>
                            <Columns
                              spaceX={{ xs: 'none', md: 'md', lg: 'lg' }}
                            >
                              <Columns.Column
                                width={{ xs: 1, md: 2 / 8, lg: 2 / 10 }}
                              >
                                <Box
                                  display="flex"
                                  justifyContent="space-between"
                                >
                                  <ArticlePublishedDate>
                                    {formatDate(
                                      new Date(publishedDate),
                                      'dd MMM yyyy',
                                    )}
                                  </ArticlePublishedDate>
                                  <Visible isHidden={{ xs: false, md: true }}>
                                    <ArticleView>
                                      View&nbsp;
                                      <FeatherArrowRightIcon
                                        size={{ xs: 'md', lg: 'lg' }}
                                      />
                                    </ArticleView>
                                  </Visible>
                                </Box>
                              </Columns.Column>

                              <Columns.Column
                                width={{ xs: 1, md: 6 / 8, lg: 8 / 10 }}
                              >
                                <Box
                                  display="flex"
                                  flexDirection="row"
                                  marginTop={{ xs: 'xxs', md: 'none' }}
                                >
                                  <ArticleTitle>{title}</ArticleTitle>

                                  <Visible isHidden={{ xs: true, md: false }}>
                                    <ArticleView>
                                      View&nbsp;
                                      <FeatherArrowRightIcon
                                        size={{ xs: 'md', lg: 'lg' }}
                                      />
                                    </ArticleView>
                                  </Visible>
                                </Box>
                              </Columns.Column>
                            </Columns>
                          </ArticlesLink>
                        </Link>
                      </li>
                    ))}
                  </ArticlesList>

                  {hasNextPage && (
                    <Box display="flex" justifyContent="center">
                      <Button
                        variant="tertiary"
                        size="lg"
                        isLoading={isFetchingNextPage}
                        onClick={() => fetchNextPage()}
                      >
                        Load more
                      </Button>
                    </Box>
                  )}
                </Stack>
              </Stack>
            </Columns.Column>
          </Columns>
        </Container>
      </Box>
    </>
  );
};

export default withContentPageLayout()(NewsPage);
